/**
 * Mover es una PROPUESTA hasta que se guarda (spec 015 · FR-024…FR-028).
 *
 * QUÉ CAMBIA, Y POR QUÉ LO PIDIÓ EL PROPIETARIO
 * ─────────────────────────────────────────────
 * Antes, soltar una cita la movía **en la agenda de verdad, al instante**. Eso
 * significa que un arrastre torpe —o probar dónde encajaría algo— ya había
 * escrito. Y una cita movida por error hay que volver a moverla a ojo, porque
 * nadie apuntó de dónde venía.
 *
 * Ahora soltar deja la cita en su sitio nuevo **en color apagado**, sin escribir
 * nada, y aparece un botón «Guardar» con cuántos cambios hay pendientes. Se puede
 * mover cinco veces y guardar una.
 *
 * SE GUARDA LO NUEVO Y TAMBIÉN LO VIEJO
 * ─────────────────────────────────────
 * `origen` no se usa para escribir: se usa para **poder deshacer** y para saber
 * si una propuesta ha dejado de serlo. Si alguien arrastra una cita y la devuelve
 * a su sitio, deja de haber propuesta — y sin guardar de dónde venía, eso no se
 * puede saber y se guardaría un cambio que no cambia nada.
 */

export interface Propuesta {
    citaId: number
    /** Adónde se ha soltado. */
    dia: string
    inicio: string
    profesionalId: number | null
    minutos: number
    /** De dónde venía. Para deshacer y para detectar el «no ha cambiado nada». */
    origen: { dia: string; inicio: string; profesionalId: number | null }
}

/** Soltar en otro día del mes. Por defecto conserva la hora; se puede pedir otra. */
export function propuestaAlCambiarDeDia(
    payload: { id: number; minutos: number; inicio: string; profesionalId: number | null; dia: string },
    diaDestino: string,
    horaNueva?: string,
): Propuesta {
    const inicio = (horaNueva ?? payload.inicio).slice(0, 5)
    return {
        citaId: payload.id,
        dia: diaDestino,
        inicio,
        profesionalId: payload.profesionalId,
        minutos: payload.minutos,
        origen: {
            dia: payload.dia,
            inicio: payload.inicio.slice(0, 5),
            profesionalId: payload.profesionalId,
        },
    }
}

/** Primer clic con citas ocultas: ampliar. El siguiente, entrar al día. */
export function clicCeldaMes(hayOcultas: boolean, yaExpandida: boolean): 'expandir' | 'entrar' {
    if (hayOcultas && !yaExpandida) return 'expandir'
    return 'entrar'
}

/** ¿La propuesta deja la cita donde ya estaba? Entonces no es una propuesta. */
export function esInmovil(p: Propuesta): boolean {
    return (
        p.dia === p.origen.dia &&
        p.inicio === p.origen.inicio &&
        p.profesionalId === p.origen.profesionalId
    )
}

/**
 * Añade o quita una propuesta.
 *
 * Devuelve un mapa nuevo: el estado de React no se muta en su sitio, y además
 * así se puede comparar con el anterior para saber si hay que redibujar.
 */
export function conPropuesta(
    actuales: ReadonlyMap<number, Propuesta>,
    p: Propuesta,
): Map<number, Propuesta> {
    const n = new Map(actuales)
    // Si vuelve a su sitio original, la propuesta se retira en vez de quedarse
    // como un cambio vacío que el botón contaría y el guardado escribiría.
    if (esInmovil(p)) n.delete(p.citaId)
    else n.set(p.citaId, p)
    return n
}

export function sinPropuesta(
    actuales: ReadonlyMap<number, Propuesta>,
    citaId: number,
): Map<number, Propuesta> {
    const n = new Map(actuales)
    n.delete(citaId)
    return n
}

/**
 * Aplica las propuestas a la lista de citas.
 *
 * La cita **se mueve de sitio en la pantalla** y se marca como propuesta, para
 * que se pinte apagada. No se duplica: enseñar la vieja y la nueva a la vez haría
 * creer que hay dos citas.
 */
export function aplicar<
    T extends { id: number; dia: string; inicio: string; minutos: number; profesionalId: number | null },
>(citas: readonly T[], propuestas: ReadonlyMap<number, Propuesta>): Array<T & { propuesta: boolean }> {
    return citas.map((c) => {
        const p = propuestas.get(c.id)
        if (!p) return { ...c, propuesta: false }
        return {
            ...c,
            dia: p.dia,
            inicio: p.inicio,
            profesionalId: p.profesionalId,
            // El fin se recalcula: la duración no cambia al mover, pero la hora
            // de fin sí, y si no se recalcula la caja se pinta con el fin viejo.
            fin: sumarMinutos(p.inicio, c.minutos),
            propuesta: true,
        }
    })
}

/** `"09:30"` + 45 → `"10:15"`. Sin `Date`: es aritmética de reloj de pared. */
export function sumarMinutos(hhmm: string, minutos: number): string {
    const [h, m] = hhmm.split(':').map(Number)
    const total = (h ?? 0) * 60 + (m ?? 0) + minutos
    const hh = Math.floor(total / 60) % 24
    return `${String(hh).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}
